'use client';

import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { When } from 'react-if';

import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { usePostAddToCart, useRemoveFromCart } from '@/components/app/cart/services/hooks';
import { useGetAddOnsInfo } from '@/components/app/product/services/hooks';
import OptionsItem from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item';
import OptionsLoading from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item/loading';
import ProductSummary from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/product-summary';
import ProductSummaryLoading from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/product-summary/loading';
import Props, {
  ADDONS_ENUM,
} from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/type';
import SmartInLineCart from '@/components/shared/components/smart-offers/inline-cart';
import { useGetSmartOffer } from '@/components/shared/components/smart-offers/services/hooks';
import SteppedDiscount from '@/components/shared/components/stepped-discount/components';

import { useAddonsStore } from '@/store/addons.store';
import { usePrerequisitesStore } from '@/store/prerequisites.store';
import { useSmartOfferStore } from '@/store/smart-offer.store';

import {
  DATA_CY_ADD_TO_CARD_POP_UP,
  DATA_CY_OPTIONS_ITEM_CONTAINER,
} from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function AddToCartModal({
  slug,
  productId,
  demoId,
  steppedDiscounts,
  cart,
}: Props) {
  const [steppedDiscount, setSteppedDiscount] = useState(steppedDiscounts);
  const [initialAddonsDisplay, setInitialAddonsDisplay] = useState(false);
  const [isUpdatingAddons, setIsUpdatingAddons] = useState(false);
  const { addons, setAddons } = useAddonsStore();
  const { prerequisites, setPrerequisites } = usePrerequisitesStore();
  const { data, isPending } = useGetAddOnsInfo({ slug, demoId });
  const [finalPrice, setFinalPrice] = useState(0);
  const [productPrice, setProductPrice] = useState(0);
  const { mutate } = usePostAddToCart();
  const { mutate: Remove } = useRemoveFromCart();
  const unselectedPrerequisites = Object.keys(prerequisites).filter(
    (id) => !prerequisites[id].selected,
  );
  const [price, setPrice] = useState(0);
  const [lastPrice, setLastPrice] = useState(0);
  const { setShowSmartOffer, showSmartOffer } = useSmartOfferStore();
  const { data: offer } = useGetSmartOffer('/cart-page');
  useEffect(() => {
    if (offer && (!Array.isArray(offer) || offer.length > 0)) {
      setShowSmartOffer(true);
    }
  }, [offer]);
  useEffect(() => {
    if (data) {
      if (data.addons && data.addons.length > 0) {
        data.addons.forEach(({ type }) => {
          if (
            (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && addons[type]?.selected) ||
            (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && addons[type]?.selected)
          ) {
            setInitialAddonsDisplay(true);
          }
        });
      }
    }
  }, []);
  useEffect(() => {
    if (data) {
      if (data.addons && data.addons.length > 0) {
        let finalPrice = data.price;
        let finalLastPrice = data.lastPrice;
        let addonSelected = false;
        data.addons.forEach(({ type, price, lastPrice }) => {
          if (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && addons[type]?.selected) {
            finalPrice = price + data.price;
            finalLastPrice = data.regularPrice;
            addonSelected = true;
          } else if (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && addons[type]?.selected) {
            finalPrice = price;
            finalLastPrice = lastPrice;
            addonSelected = true;
          }
        });

        if (addonSelected) {
          setPrice(finalPrice);
          setLastPrice(finalLastPrice);
        } else {
          setPrice(data.price);
          setLastPrice(data.lastPrice);
        }
      } else {
        setPrice(data.price);
        setLastPrice(data.lastPrice);
      }

      let calculatedPrice = data.price;
      let calculatedProductPrice = data.price;
      if (addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR]?.selected) {
        calculatedProductPrice = addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR].price;
        calculatedPrice = addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR].price;
      }
      if (addons[ADDONS_ENUM.REPEAT_PURCHASE]?.selected && data.lastPrice > 0) {
        calculatedPrice = data.lastPrice;
      }

      for (const [key, value] of Object.entries(addons)) {
        if (value.selected && key !== ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR) {
          calculatedPrice += value.price;
        }
      }
      if (Object.keys(prerequisites).length !== 0) {
        for (const prerequisite of Object.values(prerequisites)) {
          if (!prerequisite.selected) {
            calculatedPrice -= prerequisite.price;
            calculatedProductPrice -= prerequisite.price;
          }
        }
        setProductPrice(calculatedProductPrice);
      }

      setFinalPrice(calculatedPrice);
    }
  }, [data, addons]);
  const debouncedMutate = useCallback(
    debounce(
      (
        productId: string,
        newAddonsList: string[],
        unselectedPrerequisites: string[],
        key: string,
      ) => {
        if (data && !isUpdatingAddons) {
          setIsUpdatingAddons(true);
          mutate(
            {
              id: productId,
              slug: key,
              prerequisite: unselectedPrerequisites,
            },
            {
              onSuccess: (res) => {
                if (res.appliedCodeSteppedDiscount) {
                  showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
                }
                setSteppedDiscount(res.steppedDiscount);
                setIsUpdatingAddons(false);
              },
              onError: () => {
                setIsUpdatingAddons(false);
              },
            },
          );
        }
      },
      300, // 300ms delay
    ),
    [mutate, data, isUpdatingAddons],
  );
  const debouncedRemoveMutate = useCallback(
    debounce(
      (productId: string, key: string) => {
        if (data && !isUpdatingAddons) {
          setIsUpdatingAddons(true);
          Remove(
            {
              id: productId,
              slug: key,
            },
            {
              onSuccess: (res) => {
                if (res.appliedCodeSteppedDiscount) {
                  showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
                }
                setSteppedDiscount(res.steppedDiscount);
                setIsUpdatingAddons(false);
              },
              onError: () => {
                setIsUpdatingAddons(false);
              },
            },
          );
        }
      },
      300, // 300ms delay
    ),
    [mutate, data, isUpdatingAddons],
  );

  const handleAddOnsChange = useCallback(
    (key: string, checked: boolean, price: number) => {
      if (addons[key]?.selected === checked) {
        return;
      }
      const updatedAddOns = {
        ...addons,
        [key]: { selected: checked, price: price },
      };
      setAddons(updatedAddOns);

      const newAddonsList = Object.keys(updatedAddOns).filter(
        (item) => updatedAddOns[item].selected,
      );
      if (checked) {
        debouncedMutate(productId, newAddonsList, unselectedPrerequisites, key);
      } else {
        debouncedRemoveMutate(productId, key);
      }
    },
    [addons, debouncedMutate, productId, unselectedPrerequisites, setAddons],
  );
  useEffect(() => {
    if (cart?.products && cart?.products?.length > 0) {
      setAddons({
        ...addons,
        ...cart.products.reduce(
          (acc, item) => {
            if (item.addon && item.addon.relatedId === productId) {
              acc[item?.addon?.slug] = {
                selected: true,
                price: item.price,
              };
            }
            return acc;
          },
          {} as Record<string, { selected: boolean; price: number }>,
        ),
      });
    }
  }, [cart]);
  return (
    <Container
      data-cy={DATA_CY_ADD_TO_CARD_POP_UP}
      className='bg-[#F0F0F280] p-[30px] pb-[18px]'
    >
      <Container className='rounded-md bg-[#FFFFFF] p-2 pb-[9px] shadow-[0px_15px_25px_0px_#969BA41A]'>
        <Container
          data-cy={DATA_CY_OPTIONS_ITEM_CONTAINER}
          className='flex flex-col divide-y divide-dashed divide-[#7E899B33] p-3'
        >
          {isPending ? (
            <ProductSummaryLoading />
          ) : (
            data && (
              <ProductSummary
                {...data}
                price={price}
                lastPrice={lastPrice}
                finalPrice={Object.keys(prerequisites).length > 0 ? productPrice : 0}
              />
            )
          )}
          {isPending ? (
            <>
              <OptionsLoading hasCheckbox />
              <OptionsLoading hasCheckbox />
            </>
          ) : (
            data &&
            data.addons &&
            data.addons.length > 0 &&
            data.addons.map((addon) => {
              const { id, type, price, replace } = addon;
              const isSubscription =
                type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME ||
                type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR;
              const isSelected = addons[type]?.selected;

              const displayData = isSubscription && initialAddonsDisplay ? replace : addon;
              const displayPrice =
                type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && !initialAddonsDisplay
                  ? addon.price + replace.price
                  : displayData?.price;
              const disabled =
                (type !== ADDONS_ENUM.REPEAT_PURCHASE &&
                  addons[ADDONS_ENUM.REPEAT_PURCHASE]?.selected) ||
                (type === ADDONS_ENUM.REPEAT_PURCHASE &&
                  addons[ADDONS_ENUM.EXTRA_SUPPORT]?.selected) ||
                (type === ADDONS_ENUM.REPEAT_PURCHASE &&
                  addons[ADDONS_ENUM.INSTALLATION]?.selected);

              if (
                (type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && (price === 0 || !price)) ||
                (type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR && (price === 0 || !price))
              ) {
                return null;
              }
              return (
                <OptionsItem
                  key={id}
                  typeItem={type}
                  label={displayData.title}
                  subTitle={displayData.subTitle}
                  price={displayPrice}
                  lastPrice={displayData.lastPrice || 0}
                  discount={displayData.discount || 0}
                  hasCheckbox
                  checked={isSelected}
                  onChange={(checked) => handleAddOnsChange(type, checked, price)}
                  disabled={disabled}
                  noSign={isSubscription}
                  showFakeCheckbox={isSubscription && initialAddonsDisplay && isSelected}
                  showFakeChecked={isSubscription && initialAddonsDisplay && !isSelected}
                />
              );
            })
          )}
        </Container>
        {isPending ? (
          <OptionsLoading />
        ) : (
          <OptionsItem
            className='rounded-[7px] bg-[linear-gradient(180deg,#84ba521a_0%,rgba(132,186,82,0)_100%)] px-3 pb-1'
            label={productLocalization.payableAmount}
            price={finalPrice}
            noSign
          />
        )}
      </Container>
      <When condition={!!offer && offer.length !== 0 && showSmartOffer}>
        <Container>
          <SmartInLineCart data={offer} />
        </Container>
      </When>
      {steppedDiscount && (
        <SteppedDiscount
          containerClassName='px-0! mt-[1px]'
          data={steppedDiscount}
          percentClassName='md:mr-7! md:ml-[120px]! ml-[55px]!'
          amountClassName='md:mr-3! md:ml-[105px]!  ml-[40px]!'
          isModal={true}
        />
      )}
    </Container>
  );
}
